import React from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  Flex,
  theme,
} from "antd";
import {
  CheckCircleFilled,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { HeaderApp } from "../../shared";
import { useCreateProvider } from "../hooks/useCreateProvider";
import type { ProviderData } from "../types";
import SectionHeader from "../../shared/components/SectionHeader";
import openNotification from "../../../utils/openNotification";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

const FieldLabel: React.FC<{ text: string }> = ({ text }) => {
  const { token } = theme.useToken();
  return (
    <Text
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: token.colorTextSecondary,
      }}
    >
      {text}
    </Text>
  );
};

const inputStyle = (token: ReturnType<typeof theme.useToken>["token"]): React.CSSProperties => ({
  borderRadius: token.borderRadiusLG,
  background: token.colorBgContainer,
  border: `1px solid ${token.colorBorderSecondary}`,
  fontSize: 14,
  fontWeight: 500,
});

const NewProvider: React.FC = () => {
  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const { mutate: createProviderMutation, isPending: isCreating } = useCreateProvider();
  const navigate = useNavigate();

  const handleFinish = async (values: ProviderData) => {
    createProviderMutation(values, {
      onSuccess: () => {
        form.resetFields();
        navigate('/catalogo/proveedores');
        openNotification('success', 'Proveedor creado exitosamente');
      },
      onError: (error) => {
        openNotification('error', 'Error', error.message);
      }
    });
  };

  return (
    <Flex
      vertical
      style={{
        background: token.colorBgLayout,
      }}
    >
      <HeaderApp sectionName="Agregar Nuevo Proveedor" showBack />

      <Flex
        vertical
        gap={28}
        style={{
          maxWidth: 640,
          width: "100%",
          margin: "0 auto",
          padding: "28px 24px 100px",
        }}
      >
        <Form
          id="new-provider-form"
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          requiredMark={false}
          style={{ width: "100%" }}
        >
          <Flex vertical gap={16} style={{ marginBottom: 28 }}>
            <SectionHeader title="Información del Proveedor" />

            <Form.Item
              name="name_provider"
              label={<FieldLabel text="Nombre" />}
              rules={[{ required: true, message: "Campo requerido" }]}
              style={{ marginBottom: 0 }}
            >
              <Input
                size="large"
                prefix={<UserOutlined style={{ color: token.colorTextTertiary }} />}
                placeholder="Ej. Distribuidora Gómez"
                style={inputStyle(token)}
              />
            </Form.Item>

            <Form.Item
              name="email_provider"
              label={<FieldLabel text="Correo Electrónico" />}
              rules={[
                { required: true, message: "Campo requerido" },
                { type: "email", message: "Correo inválido" }
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input
                size="large"
                prefix={<MailOutlined style={{ color: token.colorTextTertiary }} />}
                placeholder="contacto@proveedor.com"
                style={inputStyle(token)}
              />
            </Form.Item>

            <Form.Item
              name="phone_number_provider"
              label={<FieldLabel text="Teléfono" />}
              style={{ marginBottom: 12 }}
            >
              <Input
                size="large"
                prefix={<PhoneOutlined style={{ color: token.colorTextTertiary }} />}
                placeholder="Ej. 123 456 7890"
                style={inputStyle(token)}
              />
            </Form.Item>
          </Flex>

          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={isCreating}
            icon={<CheckCircleFilled />}
            block
            style={{
              height: 54,
              borderRadius: token.borderRadiusLG,
              fontWeight: 800,
              fontSize: 16,
              letterSpacing: "0.01em",
              boxShadow: `0 8px 20px ${token.colorPrimaryBg}`,
            }}
          >
            Guardar Proveedor
          </Button>
        </Form>
      </Flex>
    </Flex>
  );
};

export default NewProvider;
